import splitArray from "../../../Preview/Block/BlockType/BlockParts/utils/splitArray";
import horizontalSplit from "../../../Preview/Block/BlockType/BlockParts/utils/horizontalSplit";
import { splitGutter } from "../../../Preview/Block/BlockType/BlockParts/gutterUtils";
import { clampColumnWidthPct } from "../../../utils/maxColumnWidth";
import { layoutParagraph, resolveFont, resolveLineHeightPx, toPx } from "../text";

// Copies of Song2.jsx's exported helpers, kept here so the layout module
// does not import React components.
export const getSongColumnTextAlign = (alignment, columns, index) => {
  const count = Math.max(1, Number(columns) || 1);
  const columnIndex = Math.max(0, Math.min(count - 1, Number(index) || 0));
  if (count === 1 || alignment === "center") return "center";
  if (alignment === "left") return "left";
  if (alignment === "right") return "right";
  if (alignment === "justify") {
    if (columnIndex === 0) return "left";
    if (columnIndex === count - 1) return "right";
    return "center";
  }
  if (columnIndex === 0) return "right";
  if (columnIndex === count - 1) return "left";
  return "center";
};

export const getBalancedSongTextAlign = (alignment) => {
  if (alignment === "left") return "left";
  if (alignment === "right") return "right";
  return "center";
};

// Song2's `display()`: true hides an item that the balanced row renders.
const isBalancedAway = (options, colIndex, itemIndex, marks, filterItems) => {
  if (!options.balance_columns) return false;
  if (options.columns === 2 && filterItems.length === 1) {
    return colIndex === 1 && itemIndex === marks.index1;
  }
  if (options.columns === 3 && filterItems.length === 2) {
    return (
      (colIndex === 0 && itemIndex === marks.index3_1) ||
      (colIndex === 1 && itemIndex === marks.index3_2)
    );
  }
  return false;
};

const sortSongs = (items, order) => {
  const copy = [...items];
  if (order === null) return copy;
  if (order === "ascending") {
    return copy.sort((a, b) => a?.song?.title?.localeCompare(b?.song?.title));
  }
  return copy.sort((a, b) => b?.song?.title?.localeCompare(a?.song?.title));
};

export function layoutSong(ctx) {
  const { ops, measurer, options = {}, settings, fontStyle, items, left, width, top } = ctx;
  const titleStyle = fontStyle?.songTitle || {};
  const detailStyle = fontStyle?.songDetails || {};
  const titleFont = resolveFont(titleStyle, { size: 18 });
  const detailFont = resolveFont(detailStyle, { size: 18 });
  const titleLh = resolveLineHeightPx(titleFont, toPx(titleStyle.lineHeight, NaN) + 0.23);
  const detailLh = resolveLineHeightPx(detailFont, toPx(detailStyle.lineHeight, NaN) + 0.2);
  const titleMb = toPx(titleStyle.marginBottom, 0);
  const detailMb = toPx(detailStyle.marginBottom, 0);
  const gap = toPx(options.horizontal_gap, 0);

  const songs = sortSongs(
    (items || []).filter((item) => item?.song),
    options.column_order,
  );
  const { ans: rawSplit, indexMap } =
    options.column_flow === "vertical"
      ? splitArray(songs, options)
      : horizontalSplit(songs, options);
  const split1 = rawSplit.map((column) => [...column]);
  const filterItems = split1
    .map((column, i) => (indexMap[i + 1] === true ? column[column.length - 1] : undefined))
    .filter(Boolean);
  const marks = {
    index1: split1[1]?.length - 1,
    index3_1: split1[0]?.length - 1,
    index3_2: split1[1]?.length - 1,
  };
  const columns =
    filterItems.length === 2 && options.balance_columns === "center" && split1[2]
      ? split1.map((column, i) =>
          i === 0 ? [...column, filterItems[0]] : i === 2 ? [...column, filterItems[1]] : column,
        )
      : split1;

  const columnCount = Math.max(1, Number(options.columns) || 1);
  const columnWidthPct = clampColumnWidthPct(options.column_width, columnCount);
  const totalWidthPct = Math.min(columnWidthPct * columnCount, 100);
  const tableWidth = (width * totalWidthPct) / 100;
  const tableLeft = left + (width - tableWidth) / 2;
  const cellWidth = tableWidth / Math.max(1, columns.length);
  const cellGutter = splitGutter(settings?.gutter);
  const isGutter = options.text_alignment === "gutter";

  const emitSong = (item, { x, w, y, align, maxDetailLines }) => {
    let cursor = y;
    cursor += layoutParagraph(ops, {
      measurer,
      font: titleFont,
      text: item?.song?.title,
      left: x,
      width: w,
      top: cursor,
      align,
      lineHeightPx: titleLh,
      color: titleStyle.color,
      decoration: titleStyle.textDecoration,
      breakWords: true,
    });
    cursor += titleMb;
    const details = item?.song?.details;
    let lines = typeof details === "string" ? details.trim().split("\n") : [];
    if (maxDetailLines) {
      lines = Array.from({ length: maxDetailLines }, (_, i) => lines[i]);
    }
    lines.forEach((line) => {
      cursor += layoutParagraph(ops, {
        measurer,
        font: detailFont,
        text: line,
        left: x,
        width: w,
        top: cursor,
        align,
        lineHeightPx: detailLh,
        color: detailStyle.color,
        decoration: detailStyle.textDecoration,
        breakWords: true,
      });
      cursor += detailMb;
    });
    return cursor + gap - y;
  };

  let tableHeight = 0;
  columns.forEach((columnItems, colIndex) => {
    const padLeft = columns.length >= 2 && colIndex !== 0 && isGutter ? cellGutter : 0;
    const padRight =
      columns.length >= 2 && colIndex !== columns.length - 1 && isGutter ? cellGutter : 0;
    const align = getSongColumnTextAlign(options.text_alignment, columnCount, colIndex);
    const x = tableLeft + colIndex * cellWidth + padLeft;
    const w = Math.max(0, cellWidth - padLeft - padRight);
    let y = top;
    columnItems.forEach((item, itemIndex) => {
      if (isBalancedAway(options, colIndex, itemIndex, marks, filterItems)) return;
      y += emitSong(item, { x, w, y, align });
    });
    tableHeight = Math.max(tableHeight, y - top);
  });

  let balancedHeight = 0;
  if (options.balance_columns && filterItems.length === 1 && options.columns === 2) {
    const sideAligned =
      options.text_alignment === "left" || options.text_alignment === "right";
    balancedHeight = emitSong(filterItems[0], {
      x: tableLeft,
      w: sideAligned ? tableWidth / 2 : tableWidth,
      y: top + tableHeight,
      align: getBalancedSongTextAlign(options.text_alignment),
      maxDetailLines: 4,
    });
  }
  return tableHeight + balancedHeight;
}
